import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowDown,
  FaArrowUp,
  FaBoxOpen,
  FaBuilding,
  FaChevronDown,
  FaEllipsisV,
  FaEye,
  FaGavel,
  FaGripVertical,
  FaHome,
  FaImages,
  FaIndustry,
  FaInfoCircle,
  FaLayerGroup,
  FaLightbulb,
  FaPaperPlane,
  FaRedo,
  FaShoppingBag,
  FaSitemap,
  FaTh,
  FaThList,
  FaUndo,
} from "react-icons/fa";
import {
  fetchHubHomeSections,
  hubHomeSectionExtractMessage,
  moveHubHomeSection,
  reorderHubHomeSections,
  updateHubHomeSection,
} from "../../services/hubHomeSectionAdmin.service";
import {
  bannerManagementHrefForType,
  DEFAULT_HUB_HOME_SECTIONS,
  HUB_HOME_SECTION_KEYS,
  HUB_HOME_SECTION_VISUALS,
  HUB_MODULE_TILE_OPTIONS,
  GRID_SECTION_DISPLAY_LABELS,
  isGridConfigurableSection,
  resolveFeaturedCategoryShowcaseConfig,
  resolveSectionGridConfig,
  resolveTopIndustrySlugs,
  resolveTradingCategorySelections,
  resolveVisibleModuleTileKeys,
  SELLER_PRODUCTS_PER_ROW_OPTIONS,
  SELLER_PRODUCTS_ROW_OPTIONS,
} from "../../constants/hubHomeSections";
import "../../assets/styles/master_category_admin.scss";
import "../../assets/styles/super_admin.scss";
import "../../assets/styles/super_admin_home_sections.scss";

const SECTION_ICONS = {
  [HUB_HOME_SECTION_KEYS.HERO_BANNERS]: FaImages,
  [HUB_HOME_SECTION_KEYS.MODULE_TILES]: FaTh,
  [HUB_HOME_SECTION_KEYS.SELLER_PRODUCTS]: FaBoxOpen,
  [HUB_HOME_SECTION_KEYS.SPONSORED_BANNERS]: FaImages,
  [HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES]: FaSitemap,
  [HUB_HOME_SECTION_KEYS.FEATURED_CATEGORY_SHOWCASE]: FaLayerGroup,
  [HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES]: FaThList,
  [HUB_HOME_SECTION_KEYS.QUICK_BUY_REQUEST]: FaPaperPlane,
  [HUB_HOME_SECTION_KEYS.CONTRACT_MANUFACTURERS]: FaIndustry,
  [HUB_HOME_SECTION_KEYS.ACTIVE_AUCTIONS]: FaGavel,
  [HUB_HOME_SECTION_KEYS.INFRASTRUCTURE_LEASING]: FaBuilding,
  [HUB_HOME_SECTION_KEYS.PROMO_BANNERS]: FaShoppingBag,
};

const FILTER_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "banner", label: "Banner" },
  { value: "content", label: "Content" },
];

function ToggleSwitch({ checked, disabled, onChange, label }) {
  return (
    <label className="sa-toggle" title={label}>
      <input
        type="checkbox"
        className="sa-toggle__input"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="sa-toggle__track" aria-hidden />
    </label>
  );
}

function SectionTypeBadge({ sectionKey, bannerType }) {
  const visual = HUB_HOME_SECTION_VISUALS[sectionKey];
  let label = visual?.typeLabel || "Content";
  let tone = visual?.tone || "orange";

  if (bannerType === "HOME") {
    label = "Banner - HOME";
    tone = "purple";
  } else if (bannerType === "SPONSORED") {
    label = "Banner - SPONSORED";
    tone = "blue";
  } else if (bannerType === "PROMO") {
    label = "Banner - PROMO";
    tone = "pink";
  }

  return <span className={`sa-hsm-type-badge sa-hsm-type-badge--${tone}`}>{label}</span>;
}

function RowActionsMenu({
  isModuleTiles,
  isTradingCategories,
  isTopIndustries,
  isFeaturedShowcase,
  isGridConfigurable,
  bannerType,
  onConfigureGrid,
  onClose,
}) {
  return (
    <div className="sa-hsm-row-menu" role="menu">
      {bannerType ? (
        <Link
          to={bannerManagementHrefForType(bannerType)}
          className="sa-hsm-row-menu__item"
          role="menuitem"
          onClick={onClose}
        >
          Manage ads
        </Link>
      ) : null}
      {isModuleTiles ? (
        <Link
          to="/super-admin/master-data/service-quick-links"
          className="sa-hsm-row-menu__item"
          role="menuitem"
          onClick={onClose}
        >
          Manage cards
        </Link>
      ) : null}
      {isTradingCategories ? (
        <Link
          to="/super-admin/master-data/trading-categories"
          className="sa-hsm-row-menu__item"
          role="menuitem"
          onClick={onClose}
        >
          Manage categories
        </Link>
      ) : null}
      {isTopIndustries ? (
        <Link
          to="/super-admin/master-data/top-industries"
          className="sa-hsm-row-menu__item"
          role="menuitem"
          onClick={onClose}
        >
          Manage industries
        </Link>
      ) : null}
      {isFeaturedShowcase ? (
        <Link
          to="/super-admin/master-data/featured-category-showcase"
          className="sa-hsm-row-menu__item"
          role="menuitem"
          onClick={onClose}
        >
          Manage showcase
        </Link>
      ) : null}
      {isGridConfigurable ? (
        <button
          type="button"
          className="sa-hsm-row-menu__item"
          role="menuitem"
          onClick={() => {
            onConfigureGrid();
            onClose();
          }}
        >
          Configure display
        </button>
      ) : null}
    </div>
  );
}

function SectionGridConfigModal({ open, section, rows, itemsPerRow, saving, onClose, onChange, onSave }) {
  if (!open || !section) return null;

  const sectionKey = section.sectionKey || section.section_key;
  const labels = GRID_SECTION_DISPLAY_LABELS[sectionKey] || GRID_SECTION_DISPLAY_LABELS[HUB_HOME_SECTION_KEYS.SELLER_PRODUCTS];
  const preview = resolveSectionGridConfig(sectionKey, { rows, products_per_row: itemsPerRow });

  return (
    <div className="sa-modal-backdrop" role="presentation" onClick={() => !saving && onClose()}>
      <div
        className="sa-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sa-section-grid-config-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="sa-section-grid-config-title" className="mca-section-title">
          {labels.title}
        </h2>
        <p className="sa-modal__lead">
          Set how many rows and items per row appear in this section on the home page.
        </p>

        <form
          className="sa-section-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSave();
          }}
        >
          <label className="mca-field">
            <span className="mca-field__label">Number of rows</span>
            <select
              className="mca-input"
              value={rows}
              disabled={saving}
              onChange={(e) => onChange.setRows(Number(e.target.value))}
            >
              {SELLER_PRODUCTS_ROW_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value} row{value === 1 ? "" : "s"}
                </option>
              ))}
            </select>
          </label>

          <label className="mca-field">
            <span className="mca-field__label">{labels.perRowLabel}</span>
            <select
              className="mca-input"
              value={itemsPerRow}
              disabled={saving}
              onChange={(e) => onChange.setItemsPerRow(Number(e.target.value))}
            >
              {SELLER_PRODUCTS_PER_ROW_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value} {labels.perRowOption}
                </option>
              ))}
            </select>
          </label>

          <p className="sa-modal__lead">
            Total {labels.totalLabel} on home: <strong>{preview.total_items}</strong> ({preview.rows} ×{" "}
            {preview.products_per_row})
          </p>

          <div className="sa-modal__actions">
            <button type="button" className="mca-btn mca-btn--ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="mca-btn mca-btn--primary" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SuperAdminHomeSectionManagementPage() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [activeTab, setActiveTab] = useState("layout");
  const [typeFilter, setTypeFilter] = useState("all");
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [openMenuId, setOpenMenuId] = useState(null);
  const [gridModalSection, setGridModalSection] = useState(null);
  const [gridRows, setGridRows] = useState(2);
  const [gridItemsPerRow, setGridItemsPerRow] = useState(4);
  const [savingGridDisplay, setSavingGridDisplay] = useState(false);
  const menuRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchHubHomeSections();
      setSections(rows);
      setExpandedIds(new Set(rows.map((row) => row.id)));
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not load home sections."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sortedSections = useMemo(
    () =>
      [...sections].sort(
        (a, b) =>
          Number(a.displayOrder ?? a.display_order ?? 0) -
            Number(b.displayOrder ?? b.display_order ?? 0) ||
          Number(a.id) - Number(b.id),
      ),
    [sections],
  );

  const filteredSections = useMemo(() => {
    if (typeFilter === "all") return sortedSections;
    return sortedSections.filter((section) => {
      const sectionKey = section.sectionKey || section.section_key;
      const visual = HUB_HOME_SECTION_VISUALS[sectionKey];
      return visual?.filter === typeFilter;
    });
  }, [sortedSections, typeFilter]);

  const stats = useMemo(() => {
    const total = sortedSections.length;
    const visible = sortedSections.filter((section) => section.isVisible ?? section.is_visible ?? true).length;
    return { total, visible, hidden: total - visible };
  }, [sortedSections]);

  const patchVisibility = async (section, isVisible) => {
    setSavingId(section.id);
    try {
      const updated = await updateHubHomeSection(section.id, { is_visible: isVisible });
      if (updated) {
        setSections((prev) => prev.map((row) => (row.id === section.id ? updated : row)));
        toast.success(`${section.displayName || section.display_name} ${isVisible ? "shown" : "hidden"} on home.`);
      }
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not update section visibility."));
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const moveSection = async (section, direction) => {
    setMovingId(section.id);
    try {
      const rows = await moveHubHomeSection(section.id, direction);
      setSections(rows);
      toast.success("Section order updated.");
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not move section."));
      await load();
    } finally {
      setMovingId(null);
    }
  };

  const resetDefaultOrder = async () => {
    if (!sortedSections.length) return;
    setResetting(true);
    try {
      const defaultOrder = DEFAULT_HUB_HOME_SECTIONS.map((item) => item.section_key);
      const orderedIds = [...sortedSections]
        .sort((a, b) => {
          const aKey = a.sectionKey || a.section_key;
          const bKey = b.sectionKey || b.section_key;
          return defaultOrder.indexOf(aKey) - defaultOrder.indexOf(bKey);
        })
        .map((section) => section.id);
      const rows = await reorderHubHomeSections(orderedIds);
      setSections(rows);
      toast.success("Home sections reset to default order.");
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not reset section order."));
      await load();
    } finally {
      setResetting(false);
    }
  };

  const expandAll = () => setExpandedIds(new Set(sortedSections.map((section) => section.id)));
  const collapseAll = () => setExpandedIds(new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openGridConfigModal = (section) => {
    const sectionKey = section.sectionKey || section.section_key;
    const config = resolveSectionGridConfig(sectionKey, section.sectionConfig ?? section.section_config);
    setGridModalSection(section);
    setGridRows(config.rows);
    setGridItemsPerRow(config.products_per_row);
  };

  const saveGridDisplay = async () => {
    if (!gridModalSection) return;

    setSavingGridDisplay(true);
    try {
      const updated = await updateHubHomeSection(gridModalSection.id, {
        section_config: {
          rows: gridRows,
          products_per_row: gridItemsPerRow,
        },
      });
      if (updated) {
        setSections((prev) => prev.map((row) => (row.id === gridModalSection.id ? updated : row)));
        setGridModalSection(null);
        toast.success("Section display updated.");
      }
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not update section display."));
      await load();
    } finally {
      setSavingGridDisplay(false);
    }
  };

  return (
    <div className="mca-page super-admin-page sa-hsm-page">
      <nav className="mca-breadcrumb" aria-label="Breadcrumb">
        <ol className="mca-breadcrumb__list">
          <li>
            <Link to="/super-admin/dashboard">Super Admin</Link>
          </li>
          <li aria-current="page">Home Section Management</li>
        </ol>
      </nav>

      <div className="sa-hsm-page-head">
        <header className="sa-hsm-hero">
          <div className="sa-hsm-hero__title-row">
            <span className="sa-hsm-hero__icon" aria-hidden>
              <FaHome />
            </span>
            <h1 className="sa-hsm-hero__title">Home Section Management</h1>
            <span className="sa-hsm-hero__badge">Super Admin</span>
          </div>
          <p className="sa-hsm-hero__subtitle">
            Choose which blocks appear on the public home page and the order they are shown. Banner sections link to
            Banner Management for creative uploads.
          </p>
        </header>
        <Link
          to="/home"
          className="mca-btn mca-btn--outline sa-hsm-preview-btn"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaEye aria-hidden /> Preview Home Page
        </Link>
      </div>

      <div className="sa-hsm-layout">
        <div className="sa-hsm-main">
          <div className="sa-hsm-card">
            <div className="sa-hsm-tabs" role="tablist" aria-label="Home section views">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "layout"}
                className={`sa-hsm-tabs__btn${activeTab === "layout" ? " sa-hsm-tabs__btn--active" : ""}`}
                onClick={() => setActiveTab("layout")}
              >
                Home Page Layout
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "guide"}
                className={`sa-hsm-tabs__btn${activeTab === "guide" ? " sa-hsm-tabs__btn--active" : ""}`}
                onClick={() => setActiveTab("guide")}
              >
                Section Guide
              </button>
            </div>

            {activeTab === "layout" ? (
              <>
                <div className="sa-hsm-toolbar">
                  <h2 className="sa-hsm-toolbar__title">Manage sections</h2>
                  <div className="sa-hsm-toolbar__actions">
                    <label className="sa-hsm-filter">
                      <span className="visually-hidden">Filter by type</span>
                      <select
                        className="sa-hsm-filter__select"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                      >
                        {FILTER_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <FaChevronDown className="sa-hsm-filter__chevron" aria-hidden />
                    </label>
                  </div>
                </div>

                {loading ? (
                  <p className="sa-placeholder-copy sa-hsm-loading">Loading sections…</p>
                ) : (
                  <div className="sa-hsm-table-wrap">
                    <table className="sa-hsm-table">
                      <thead>
                        <tr>
                          <th scope="col" className="sa-hsm-table__col-order">
                            Order
                          </th>
                          <th scope="col" className="sa-hsm-table__col-section">
                            Section
                          </th>
                          <th scope="col" className="sa-hsm-table__col-type">
                            Type
                          </th>
                          <th scope="col" className="sa-hsm-table__col-status">
                            Status
                          </th>
                          <th scope="col" className="sa-hsm-table__col-actions">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSections.map((section) => {
                          const displayName = section.displayName || section.display_name;
                          const description = section.description || "";
                          const bannerType = section.bannerType || section.banner_type;
                          const isVisible = section.isVisible ?? section.is_visible ?? true;
                          const sectionKey = section.sectionKey || section.section_key;
                          const isModuleTiles = sectionKey === HUB_HOME_SECTION_KEYS.MODULE_TILES;
                          const isTradingCategories = sectionKey === HUB_HOME_SECTION_KEYS.TRADING_CATEGORIES;
                          const isTopIndustries = sectionKey === HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES;
                          const isFeaturedShowcase =
                            sectionKey === HUB_HOME_SECTION_KEYS.FEATURED_CATEGORY_SHOWCASE;
                          const isGridConfigurable = isGridConfigurableSection(sectionKey);
                          const gridDisplay = isGridConfigurable
                            ? resolveSectionGridConfig(sectionKey, section.sectionConfig ?? section.section_config)
                            : null;
                          const visual = HUB_HOME_SECTION_VISUALS[sectionKey] || { tone: "orange" };
                          const Icon = SECTION_ICONS[sectionKey] || FaHome;
                          const isExpanded = expandedIds.has(section.id);
                          const visibleTileKeys = isModuleTiles
                            ? resolveVisibleModuleTileKeys(section.sectionConfig ?? section.section_config)
                            : [];
                          const tileLabelByKey = Object.fromEntries(
                            HUB_MODULE_TILE_OPTIONS.map((option) => [option.key, option.label]),
                          );
                          const visibleTileSummary = visibleTileKeys
                            .map((key) => tileLabelByKey[key] || key)
                            .join(" → ");
                          const tradingCategorySelections = isTradingCategories
                            ? resolveTradingCategorySelections(section.sectionConfig ?? section.section_config)
                            : [];
                          const topIndustrySlugs = isTopIndustries
                            ? resolveTopIndustrySlugs(section.sectionConfig ?? section.section_config)
                            : [];
                          const featuredShowcaseConfig = isFeaturedShowcase
                            ? resolveFeaturedCategoryShowcaseConfig(
                                section.sectionConfig ?? section.section_config,
                              )
                            : null;
                          const busy = savingId === section.id || movingId === section.id;
                          const actualIndex = sortedSections.findIndex((row) => row.id === section.id);

                          return (
                            <tr key={section.id} className={isExpanded ? "" : "sa-hsm-table__row--collapsed"}>
                              <td className="sa-hsm-table__col-order">
                                <div className="sa-hsm-table__order-cell">
                                  <span className="sa-hsm-table__grip" aria-hidden>
                                    <FaGripVertical />
                                  </span>
                                  <span className="sa-hsm-table__order-num">{actualIndex + 1}</span>
                                </div>
                              </td>
                              <td className="sa-hsm-table__col-section">
                                <button
                                  type="button"
                                  className="sa-hsm-section-cell"
                                  onClick={() => toggleExpanded(section.id)}
                                  aria-expanded={isExpanded}
                                >
                                  <span className={`sa-hsm-section-icon sa-hsm-section-icon--${visual.tone}`}>
                                    <Icon aria-hidden />
                                  </span>
                                  <span className="sa-hsm-section-copy">
                                    <span className="sa-hsm-section-copy__title">{displayName}</span>
                                    {description ? (
                                      <span className="sa-hsm-section-copy__desc">{description}</span>
                                    ) : null}
                                    {isExpanded && isModuleTiles ? (
                                      <>
                                        <span className="sa-hsm-section-copy__meta">
                                          Showing {visibleTileKeys.length} of {HUB_MODULE_TILE_OPTIONS.length}{" "}
                                          quick-link cards
                                        </span>
                                        <span className="sa-hsm-section-copy__flow">{visibleTileSummary}</span>
                                      </>
                                    ) : null}
                                    {isExpanded && isGridConfigurable && gridDisplay ? (
                                      <span className="sa-hsm-section-copy__meta">
                                        Display: {gridDisplay.rows} row
                                        {gridDisplay.rows === 1 ? "" : "s"} × {gridDisplay.products_per_row} per row (
                                        {gridDisplay.total_items} total)
                                      </span>
                                    ) : null}
                                    {isExpanded && isTradingCategories ? (
                                      <span className="sa-hsm-section-copy__meta">
                                        {tradingCategorySelections.length
                                          ? `${tradingCategorySelections.length} manually selected categories`
                                          : "Automatic trending categories (no manual selection saved)"}
                                      </span>
                                    ) : null}
                                    {isExpanded && isTopIndustries ? (
                                      <span className="sa-hsm-section-copy__meta">
                                        {topIndustrySlugs.length
                                          ? `${topIndustrySlugs.length} manually selected industries`
                                          : "Automatic trending industries (no manual selection saved)"}
                                      </span>
                                    ) : null}
                                    {isExpanded && isFeaturedShowcase ? (
                                      <span className="sa-hsm-section-copy__meta">
                                        {featuredShowcaseConfig?.category_id
                                          ? `Featured category #${featuredShowcaseConfig.category_id} · ${featuredShowcaseConfig.subcategory_ids.length} sub-categories`
                                          : "No featured category configured yet"}
                                      </span>
                                    ) : null}
                                  </span>
                                </button>
                              </td>
                              <td className="sa-hsm-table__col-type">
                                <SectionTypeBadge sectionKey={sectionKey} bannerType={bannerType} />
                              </td>
                              <td className="sa-hsm-table__col-status">
                                <div className="sa-hsm-status-cell">
                                  <ToggleSwitch
                                    checked={isVisible}
                                    disabled={busy}
                                    onChange={(checked) => patchVisibility(section, checked)}
                                    label={`Toggle visibility for ${displayName}`}
                                  />
                                  <span
                                    className={`sa-hsm-status-label${
                                      isVisible ? " sa-hsm-status-label--on" : " sa-hsm-status-label--off"
                                    }`}
                                  >
                                    {isVisible ? "Visible" : "Hidden"}
                                  </span>
                                </div>
                              </td>
                              <td className="sa-hsm-table__col-actions">
                                <div className="sa-hsm-table__actions">
                                  <button
                                    type="button"
                                    className="sa-hsm-icon-btn"
                                    disabled={busy || actualIndex === 0}
                                    onClick={() => moveSection(section, "up")}
                                    aria-label={`Move ${displayName} up`}
                                  >
                                    <FaArrowUp aria-hidden />
                                  </button>
                                  <button
                                    type="button"
                                    className="sa-hsm-icon-btn"
                                    disabled={busy || actualIndex === sortedSections.length - 1}
                                    onClick={() => moveSection(section, "down")}
                                    aria-label={`Move ${displayName} down`}
                                  >
                                    <FaArrowDown aria-hidden />
                                  </button>
                                  <div className="sa-hsm-menu-wrap" ref={openMenuId === section.id ? menuRef : null}>
                                    <button
                                      type="button"
                                      className="sa-hsm-icon-btn"
                                      aria-label={`More actions for ${displayName}`}
                                      aria-expanded={openMenuId === section.id}
                                      onClick={() =>
                                        setOpenMenuId((current) => (current === section.id ? null : section.id))
                                      }
                                    >
                                      <FaEllipsisV aria-hidden />
                                    </button>
                                    {openMenuId === section.id ? (
                                      <RowActionsMenu
                                        isModuleTiles={isModuleTiles}
                                        isTradingCategories={isTradingCategories}
                                        isTopIndustries={isTopIndustries}
                                        isFeaturedShowcase={isFeaturedShowcase}
                                        isGridConfigurable={isGridConfigurable}
                                        bannerType={bannerType}
                                        onConfigureGrid={() => openGridConfigModal(section)}
                                        onClose={() => setOpenMenuId(null)}
                                      />
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <p className="sa-hsm-table-tip">
                      Tip: Use drag handle <FaGripVertical aria-hidden /> to reorder sections
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="sa-hsm-guide-panel">
                <ul className="sa-home-section-guide">
                  <li>
                    <strong>Hero / Mid / Bottom banners</strong> — upload and schedule creatives in{" "}
                    <Link to="/super-admin/master-data/banner-management">Banner Management</Link>.
                  </li>
                  <li>
                    <strong>Service Quick Links</strong> — manage card visibility and order on the{" "}
                    <Link to="/super-admin/master-data/service-quick-links">Service Quick Links</Link> page.
                  </li>
                  <li>
                    <strong>Trending Categories</strong> — manage category and sub-category selection on the{" "}
                    <Link to="/super-admin/master-data/trading-categories">Trending Categories</Link> page.
                  </li>
                  <li>
                    <strong>Top Industries</strong> — choose which CM industries appear above Contract Manufacturers on
                    the <Link to="/super-admin/master-data/top-industries">Top Industries</Link> page.
                  </li>
                  <li>
                    <strong>Featured Category Showcase</strong> — pick one highlighted category and sub-category grid on
                    the{" "}
                    <Link to="/super-admin/master-data/featured-category-showcase">Featured Category Showcase</Link>{" "}
                    page.
                  </li>
                  <li>
                    <strong>Seller Products, Top Industries, Contract Manufacturers, Active Auctions, Infrastructure Leasing, Featured Category Showcase</strong> —
                    use <strong>Configure display</strong> to set rows and items per row.
                  </li>
                  <li>
                    <strong>Other content sections</strong> — fed from live marketplace data.
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <aside className="sa-hsm-aside" aria-label="Home section tools">
          <div className="sa-hsm-widget">
            <h3 className="sa-hsm-widget__title">Layout Summary</h3>
            <dl className="sa-hsm-summary">
              <div className="sa-hsm-summary__row">
                <dt>Total Sections</dt>
                <dd>{stats.total}</dd>
              </div>
              <div className="sa-hsm-summary__row sa-hsm-summary__row--visible">
                <dt>Visible Sections</dt>
                <dd>{stats.visible}</dd>
              </div>
              <div className="sa-hsm-summary__row sa-hsm-summary__row--hidden">
                <dt>Hidden Sections</dt>
                <dd>{stats.hidden}</dd>
              </div>
            </dl>
          </div>

          <div className="sa-hsm-notice sa-hsm-notice--info">
            <FaInfoCircle aria-hidden />
            <p>Changes are saved automatically. Use Preview to see how the homepage looks.</p>
          </div>

          <div className="sa-hsm-widget">
            <h3 className="sa-hsm-widget__title">Quick Actions</h3>
            <div className="sa-hsm-quick-actions">
              <button
                type="button"
                className="sa-hsm-quick-actions__btn"
                onClick={() => void resetDefaultOrder()}
                disabled={resetting || loading}
              >
                <FaUndo aria-hidden /> Reset to Default Order
              </button>
              <button type="button" className="sa-hsm-quick-actions__btn" onClick={expandAll} disabled={loading}>
                <FaRedo aria-hidden /> Expand All
              </button>
              <button type="button" className="sa-hsm-quick-actions__btn" onClick={collapseAll} disabled={loading}>
                <FaChevronDown aria-hidden /> Collapse All
              </button>
            </div>
          </div>

          <div className="sa-hsm-notice sa-hsm-notice--tips">
            <FaLightbulb aria-hidden />
            <div>
              <strong>Tips</strong>
              <ul>
                <li>Drag and drop to reorder sections</li>
                <li>Toggle the switch to show/hide sections</li>
                <li>Manage banner content from Banner Management</li>
                <li>Use the row menu to configure display or manage section content</li>
              </ul>
            </div>
          </div>
        </aside>
      </div>

      <footer className="sa-hsm-footer">© 2025 Trocanet.com | All rights reserved.</footer>

      <SectionGridConfigModal
        open={Boolean(gridModalSection)}
        section={gridModalSection}
        rows={gridRows}
        itemsPerRow={gridItemsPerRow}
        saving={savingGridDisplay}
        onClose={() => !savingGridDisplay && setGridModalSection(null)}
        onChange={{ setRows: setGridRows, setItemsPerRow: setGridItemsPerRow }}
        onSave={() => void saveGridDisplay()}
      />
    </div>
  );
}
